import { getTradingCompetitionSubaccountAddr } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { useUserSubaccountsQuery } from "@/contexts/queries/user-subaccounts";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { addressComparisonKey } from "@/utils/address";

import { useDecibelWallet } from "./use-decibel-wallet";
import { useUserPreferences } from "./use-user-preferences";

export const useSubaccountsWithPrimary = () => {
  const t = useTranslations("accounts.accountColumns");
  const { accountAddress } = useDecibelWallet();
  const { primarySubaccountAddress } = useWriteDexSdk();
  const { customSubaccountNames } = useUserPreferences();
  const { data: subaccounts = [], isPending: isLoadingSubaccounts } = useUserSubaccountsQuery({
    ownerAddr: accountAddress,
  });

  const competitionSubaccountAddress = accountAddress
    ? getTradingCompetitionSubaccountAddr(accountAddress)
    : undefined;

  const subaccountsWithPrimary = useMemo(() => {
    // Indexer rows can be zero-trimmed; the derived addresses are canonical.
    const primaryKey = primarySubaccountAddress && addressComparisonKey(primarySubaccountAddress);
    const competitionKey =
      competitionSubaccountAddress && addressComparisonKey(competitionSubaccountAddress);

    const primarySubaccount =
      accountAddress && primarySubaccountAddress
        ? {
            subaccount_address: primarySubaccountAddress,
            custom_label: customSubaccountNames[primarySubaccountAddress] ?? t("primary"),
            primary_account_address: accountAddress,
            is_primary: true,
            is_active: true,
          }
        : undefined;

    const labeledSubaccounts = subaccounts
      .filter(
        // Primary stays visible even when inactive so the picker is never empty.
        (subaccount) =>
          subaccount.is_active ||
          addressComparisonKey(subaccount.subaccount_address) === primaryKey,
      )
      .map((subaccount) => {
        const customLabel = customSubaccountNames[subaccount.subaccount_address];
        if (addressComparisonKey(subaccount.subaccount_address) === primaryKey) {
          return {
            ...subaccount,
            custom_label: customLabel ?? t("primary"),
          };
        } else if (addressComparisonKey(subaccount.subaccount_address) === competitionKey) {
          return {
            ...subaccount,
            custom_label: customLabel ?? t("competitionSubaccountLabel"),
          };
        } else {
          return {
            ...subaccount,
            custom_label:
              customLabel ??
              t("subaccountLabel", { label: subaccount.subaccount_address.slice(0, 6) }),
          };
        }
      });

    if (
      primarySubaccount &&
      !labeledSubaccounts.some(
        (subaccount) => addressComparisonKey(subaccount.subaccount_address) === primaryKey,
      )
    ) {
      labeledSubaccounts.unshift(primarySubaccount);
    }

    return labeledSubaccounts;
  }, [
    subaccounts,
    primarySubaccountAddress,
    accountAddress,
    customSubaccountNames,
    competitionSubaccountAddress,
    t,
  ]);

  return {
    subaccounts: subaccountsWithPrimary,
    isLoadingSubaccounts,
  };
};
